<script>
	import { onMount } from 'svelte';
	import { push } from 'svelte-spa-router';
	import fetchData from '../../api.js';
	import UserStore from '../../stores/user';
	import { formatDate } from '../../lib/format';
	import AppShell from '../../components/AppShell.svelte';
	import Icon from '../../components/Icon.svelte';

	let users = [];
	let loading = true;
	let editing = null;
	let busy = false;
	let error = '';
	let saved = '';

	async function load() {
		const res = await fetchData({ action: 'agents_users_list', token: $UserStore.data });
		users = (res && res.data) || [];
		loading = false;
	}

	function startNew() {
		error = '';
		editing = { id: 0, name: '', email: '', role: 'agent', active: true, can_returns: false, password: '' };
	}

	function startEdit(u) {
		error = '';
		editing = { id: Number(u.id), name: u.name, email: u.email, role: u.role, active: Number(u.active) === 1, can_returns: Number(u.can_returns) === 1, password: '' };
	}

	// a temporary password the admin can read out to the agent
	function generate() {
		const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
		const bytes = crypto.getRandomValues(new Uint32Array(10));
		editing.password = Array.from(bytes, (b) => chars[b % chars.length]).join('');
	}

	async function save() {
		error = '';
		busy = true;
		const res = await fetchData({ action: 'agents_users_save', token: $UserStore.data, user: editing });
		busy = false;
		if (res && Number(res.success) === 1) {
			users = res.data || users;
			saved = editing.id ? 'המשתמש עודכן' : 'המשתמש נוסף';
			editing = null;
			setTimeout(() => (saved = ''), 2500);
		} else {
			error = (res && res.msg) || 'השמירה נכשלה. נסה שוב.';
		}
	}

	onMount(() => {
		if (!$UserStore || !$UserStore.data) return push('/login');
		if ($UserStore.role !== 'admin') return push('/account');
		load();
	});
</script>

<AppShell title="ניהול משתמשים">
	<div class="top">
		<p class="muted">כל סוכן נכנס עם המייל והסיסמה שלו ורואה רק את הסלים וההזמנות שלו. מנהל רואה את כולם.</p>
		<button class="btn-primary" on:click={startNew}><Icon name="plus" size={20} /> משתמש חדש</button>
	</div>
	{#if saved}<div class="alert-ok msg">{saved}</div>{/if}

	{#if loading}
		<div class="spinner" />
	{:else}
		<div class="table">
			<div class="head"><span>שם</span><span>מייל</span><span>תפקיד</span><span>כניסה אחרונה</span><span /></div>
			{#each users as u (u.id)}
				<div class="row" class:off={Number(u.active) !== 1}>
					<span class="name">{u.name}{#if Number(u.id) === Number($UserStore.id)} <small class="muted">(את/ה)</small>{/if}</span>
					<span dir="ltr" class="email">{u.email}</span>
					<span>
						{#if Number(u.active) !== 1}<span class="badge-warn">חסום</span>
						{:else if u.role === 'admin'}<span class="badge-dark">מנהל</span>
						{:else}<span class="badge-ok">סוכן</span>{/if}
						{#if Number(u.can_returns) === 1}<span class="badge-ok">החזרות</span>{/if}
						{#if Number(u.must_change_password) === 1}<small class="muted pending">ממתין לסיסמה חדשה</small>{/if}
					</span>
					<span class="muted">{u.last_login_at ? formatDate(u.last_login_at) : 'עוד לא נכנס'}</span>
					<span><button class="btn-ghost small" on:click={() => startEdit(u)}>עריכה</button></span>
				</div>
			{/each}
		</div>
	{/if}
</AppShell>

{#if editing}
	<div class="backdrop over-sheet" on:click={() => (editing = null)} />
	<form class="modal" on:submit|preventDefault={save}>
		<div class="modal-head">
			<h2>{editing.id ? 'עריכת משתמש' : 'משתמש חדש'}</h2>
			<button type="button" class="icon-btn" on:click={() => (editing = null)} aria-label="סגירה"><Icon name="close" /></button>
		</div>
		<div class="modal-body form">
			<label><span>שם מלא</span><input bind:value={editing.name} required /></label>
			<label><span>מייל (משמש לכניסה)</span><input type="email" bind:value={editing.email} dir="ltr" required /></label>
			<div class="field">
				<span>תפקיד</span>
				<div class="seg">
					<button type="button" class:on={editing.role === 'agent'} on:click={() => (editing.role = 'agent')}>סוכן</button>
					<button type="button" class:on={editing.role === 'admin'} on:click={() => (editing.role = 'admin')}>מנהל</button>
				</div>
			</div>
			<div class="field">
				<span>מסך החזרות סחורה (מחיר הזיכוי לפי החשבוניות)</span>
				<div class="seg">
					<button type="button" class:on={editing.can_returns} on:click={() => (editing.can_returns = true)}>מורשה</button>
					<button type="button" class:on={!editing.can_returns} on:click={() => (editing.can_returns = false)}>לא מורשה</button>
				</div>
			</div>
			<label>
				<span>{editing.id ? 'סיסמה זמנית חדשה (להשאיר ריק כדי לא לשנות)' : 'סיסמה זמנית (לפחות 8 תווים)'}</span>
				<div class="pw">
					<input type="text" bind:value={editing.password} dir="ltr" autocomplete="off" required={!editing.id} />
					<button type="button" class="btn-ghost small" on:click={generate}>יצירה אוטומטית</button>
				</div>
				<small class="muted">את הסיסמה הזמנית מוסרים לסוכן. בכניסה הראשונה הוא יתבקש לבחור סיסמה משלו.</small>
			</label>
			{#if editing.id}
				<div class="field">
					<span>מצב</span>
					<div class="seg">
						<button type="button" class:on={editing.active} on:click={() => (editing.active = true)}>פעיל</button>
						<button type="button" class:on={!editing.active} on:click={() => (editing.active = false)}>חסום</button>
					</div>
				</div>
			{/if}
			{#if error}<div class="alert-error">{error}</div>{/if}
		</div>
		<div class="foot">
			<button type="button" class="btn-ghost" on:click={() => (editing = null)}>ביטול</button>
			<button type="submit" class="btn-primary" disabled={busy}>{busy ? 'שומר…' : 'שמירה'}</button>
		</div>
	</form>
{/if}

<style>
	.top { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 16px; }
	.top p { margin: 0; }
	.msg { margin-bottom: 12px; }
	.table { display: block; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius); overflow: hidden; }
	.head, .row { display: grid; grid-template-columns: 1fr 1.3fr 160px 170px 110px; gap: 12px; align-items: center; padding: 0 20px; }
	.head { height: 48px; color: var(--muted); font-size: 14px; border-bottom: 1px solid var(--line); }
	.row { min-height: 68px; border-bottom: 1px solid var(--line); }
	.row:last-child { border-bottom: 0; }
	.row.off { opacity: .55; }
	.name { font-weight: 700; }
	.email { text-align: right; overflow: hidden; text-overflow: ellipsis; }
	.pending { display: block; font-size: 12px; margin-top: 4px; }
	.form { display: flex; flex-direction: column; gap: 14px; }
	label, .field { display: flex; flex-direction: column; gap: 6px; }
	label > span, .field > span { font-size: 15px; color: var(--ink-2); }
	input { height: 52px; border: 1px solid var(--line-strong); border-radius: 12px; padding: 0 14px; font-size: 18px; }
	input[dir='ltr'] { text-align: left; }
	input:focus { outline: none; border-color: var(--ink); box-shadow: inset 0 0 0 1px var(--ink); }
	.pw { display: flex; gap: 8px; }
	.pw input { flex: 1; min-width: 0; }
	.foot { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid var(--line); }
	@media (max-width: 1000px) {
		.head, .row { grid-template-columns: 1fr 140px 110px; }
		.head span:nth-child(2), .head span:nth-child(4), .row > span:nth-child(2), .row > span:nth-child(4) { display: none; }
	}
</style>
